import React, { Reducer, useCallback, useEffect, useReducer, useState } from "react";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { initializeRTC } from "./RTCConnection";
import { RealtimeClientEvent, RealtimeServerEvent, SessionUpdateEvent, ConversationItem } from 'openai/resources/beta/realtime/realtime';

export type ChatEvent = ConversationItem & {
  meta: { time: number, hidden?: boolean }
};

type ControlSignal = { type: 'clear-chat' };

/**
 * Low-level hook that allows working directly with the events send to/from OpenAI Realtime using WebRTC.
 *
 * Only use this if you want to create an entire new mode of interaction. Otherwise, use `useChatChannel()`.
 */
export function useChatEvents<S>(reducer: Reducer<S, RealtimeClientEvent | RealtimeServerEvent | ControlSignal>, initial: S, sessionType?: 'survey' | 'distro') {
  const tokenEndpoint = sessionType === 'distro' ? '/program/admin/ai_chat/create' : '/coach/conversation';
  const createConversation = usePost(tokenEndpoint);
  const [channel, setChannel] = useState<RTCDataChannel | null>(null);
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const [channelState, setChannelState] = useState<{ status: 'closed' | 'open' | 'initializing' } | { status: 'error', reason: string }>({ status: 'closed' });
  const [state, dispatch] = useReducer(reducer, initial);
  const [enabled, setEnabled] = useState(false);

  useAsyncEffect(async (cancel) => {

    if (!enabled) {
      return;
    }

    const session = await createConversation({
      modality: 'text',
      persistConversation: false
    }, undefined, cancel);

    const token = session.token;

    if (!token) {
      return;
    }

    setChannelState({ status: 'initializing' });

    const { dataChannel, pc } = await initializeRTC(token, {
      onReady() {
        setChannel(dataChannel);
        setPeerConnection(pc);
        setChannelState({ status: 'open' });
      },
      onConnectionError(e) {
        setChannelState({ status: 'error', reason: e });
        setPeerConnection(null);
        setChannel(null);
      },
      onClose() {
        setChannelState({ status: 'closed' });
        setPeerConnection(null);
        setChannel(null);
      },
      onError() {
        setChannelState({ status: 'error', reason: 'unknown' });
        setPeerConnection(null);
        setChannel(null);
      }
    });

    cancel.addEventListener('abort', () => {
      dataChannel.close();
      setChannelState({ status: 'closed' })
    }, { once: true, passive: true });

  }, [enabled]);

  useEffect(() => {

    const messageHandler = (msg: MessageEvent<any>) => dispatch(JSON.parse(msg.data));

    if (channel) {
      channel.addEventListener('message', messageHandler);
    }

    return () => channel?.removeEventListener('message', messageHandler);
  }, [channel]);

  const sendEvent = useCallback((message: RealtimeClientEvent | ControlSignal) => {
    if (!channel || channelState.status !== 'open' || channel.readyState !== 'open') {
      return;
    }

    dispatch(message);
    channel.send(JSON.stringify(message));

  }, [dispatch, channel, channelState]);

  const manageAudioSubscription = useCallback((action: 'subscribe' | 'unsubscribe', fn: (event: RTCTrackEvent) => void) => {
    if (action === 'subscribe') {
      peerConnection?.addEventListener('track', fn);
    } else {
      peerConnection?.removeEventListener('track', fn);
    }
  }, []);

  return { manageAudioSubscription, setEnabled, enabled, sendEvent, state, status: channelState };
}

/**
 * Wraps the Chat Events from WebRTC for a more structured hook.
 * Some methods, such as Enabling and Monitoring the Connection and Audio Subscription 
 * are passed through to the consumer of the hook.
 *
 * Provides methods to Send and Manage Messages.
 */
export function useChatChannel(sessionType: 'survey' | 'distro' = 'survey') {

  const { manageAudioSubscription, setEnabled, enabled, state, sendEvent, status } = useChatEvents(({ map }, action) => {
    switch (action.type) {
      case "clear-chat":
        return {
          map: new Map(),
          toolCalls: new Map(),
          list: []
        };
      case "error":
        console.error(action);
        break;
      case "conversation.item.create": {
        map.set(uniqueOrderedId(map), {
          ...action.item,
          role: 'user', // 'system' is reserved for hiding output for now, hence why we don't use `action.role`.
          meta: {
            time: Date.now(),
            hidden: action.item.type !== 'message' || action.item.role === 'system'
          }
        });
        break;
      }
      case "response.output_item.added": {
        // server added a new response
        if (action.response_id) {
          map.set(action.response_id, {
            ...action.item,
            meta: {
              time: Date.now(),
              hidden: !!action.item.call_id
            }
          });
        }
        break;
      }
      case "response.text.delta": {
        const item = action.response_id ? map.get(action.response_id) : undefined;
        if (item) {
          const content = item.content = item.content ?? [];
          content[action.content_index] = content[action.content_index] ?? {};
          content[action.content_index].text = [content[action.content_index].text ?? "", action.delta].join("");
        }
        break;
      }
      default:
        break;
    }
    return { map, list: Array.from(map.values()) };
  }, { map: new Map(), list: [], toolCalls: new Map() } as {
    map: Map<string, ChatEvent>,
    list: ChatEvent[]
  }, sessionType);

  const updateSession = useCallback((session: SessionUpdateEvent['session']) => {
    sendEvent({
      type: 'session.update',
      session
    });
  }, [sendEvent]);

  const sendChatMessage = useCallback((role: 'user' | 'system', text: string, createResponse?: boolean) => {
    const conversationItem: ConversationItem = {
      type: "message",
      role,
      content: [
        {
          type: "input_text",
          text
        }
      ]
    };

    sendEvent({
      type: "conversation.item.create",
      item: conversationItem
    });

    if (role === 'user' || createResponse) {
      sendEvent({
        type: "response.create"
      });
    }
  }, [sendEvent]);

  const restart = useCallback(() => {
    setEnabled(false);
    sendEvent({ type: 'clear-chat' });
    setTimeout(() => setEnabled(true));
  }, [sendEvent, setEnabled]);

  return {
    manageAudioSubscription,
    setEnabled,
    enabled,
    sendChatMessage,
    updateSession,
    messages: state.list,
    status,
    restart
  };
}

export const ChatChannelContext = React.createContext({} as ChatChannel);

function uniqueOrderedId(existing: Map<string, any>) {

  const now = Date.now();

  // generate a (mostly) collision proof ID that creates a natural sort order.
  // - 1743692588848_000
  // - 1743692588848_001
  // - 1743692588849_000
  let existingItemWithTimestamp;
  let dedupeIndex = 0;
  let id: string | undefined;
  do {
    id = `${now}_${dedupeIndex.toString().padStart(3, '0')}`;
    existingItemWithTimestamp = existing.get(`convo_${id}`);
    dedupeIndex += 1;
  } while (existingItemWithTimestamp);

  return id;
}

export type ChatChannel = ReturnType<typeof useChatChannel>;

